import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { IUserMembership } from '@nimbus-portal/features/user-header/models/user-header-state.model';
import { UserHeaderStateService } from '@nimbus-portal/features/user-header/services/user-header-state.service';
import { UserTeamComponent } from '@nimbus-portal/features/user-header/components/user-team/user-team.component';
import { UserTeamVariant } from '@nimbus-portal/features/user-header/components/user-team/user-team-variant.enum';
import { HeaderBarNavigationService } from '@nimbus-portal/common/services/header-bar-navigation.service';
import { HeaderBarPinnedServicesService } from '@nimbus-portal/common/services/header-bar-pinned-services.service';

@Component({
  standalone: true,
  selector: 'nimbus-header-bar-pinned-services',
  templateUrl: './header-bar-pinned-services.component.html',
  styleUrl: './header-bar-pinned-services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, SharedUIModule, TranslateModule, UserTeamComponent],
})
export class HeaderBarPinnedServicesComponent implements OnInit {
  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly UserTeamVariant = UserTeamVariant;

  public pinnedServices$: Observable<IUserMembership[]>;
  public toggleUserEnabilityTeamIds$: Observable<Set<string>>;
  public isOnAttendantConsole$: Observable<boolean>;

  public constructor(
    private readonly userHeaderStateService: UserHeaderStateService,
    private readonly pinnedServicesService: HeaderBarPinnedServicesService,
    private readonly permissionManagerService: IPermissionManagerService,
    private readonly headerBarNavigationService: HeaderBarNavigationService,
  ) {}

  public ngOnInit(): void {
    this.isOnAttendantConsole$ = this.headerBarNavigationService.isOnAttendantConsole$;
    this.toggleUserEnabilityTeamIds$ = this.permissionManagerService
      .getTeamIds$(NimbusPermission.ToggleUserEnability)
      .pipe(map((ids) => new Set(ids)));
    this.pinnedServices$ = combineLatest([this.userHeaderStateService.teams$, this.pinnedServicesService.pinnedTeamIds$]).pipe(
      // Preserve pin order and skip pins whose membership no longer exists.
      map(([teams, pinnedIds]) =>
        pinnedIds.map((id) => teams.find((team) => team.teamId === id)).filter((team): team is IUserMembership => !!team),
      ),
    );
  }

  // Only fires when the tile is clickable (on the attendant console).
  public onPinnedClick(service: IUserMembership): void {
    this.headerBarNavigationService.openTeamInAttendantConsole(service.teamId);
  }

  public unpin(service: IUserMembership, event: Event): void {
    // Keep the tile's navigation click from firing when unpinning.
    event.stopPropagation();
    this.pinnedServicesService.toggle(service.teamId);
  }
}
